import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { apiFetch, type TemplateVariable } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { useConfirmation } from '@/hooks/useConfirmation';

export interface StarterDraftPatch {
  Name?: string;
  Description?: string;
  Region?: string;
  TargetModule?: string;
  Body?: string;
  Variables?: TemplateVariable[];
}
interface Proposal {
  starterId: string;
  requiresReview: boolean;
  differences: { field: string; current: unknown; proposed: unknown }[];
}
const buttonStyle: CSSProperties = { padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' };
const allowed = new Set(['Name', 'Description', 'Region', 'TargetModule', 'Body', 'Variables']);

function variablesFrom(value: unknown): TemplateVariable[] {
  const parsed: unknown = typeof value === 'string' ? JSON.parse(value) : value;
  if (!Array.isArray(parsed)) throw new Error('The proposed variables are not a valid list.');
  return parsed.map((item: unknown) => {
    if (typeof item !== 'object' || item === null || !('name' in item) || typeof item.name !== 'string' || !item.name.trim() || !('required' in item) || typeof item.required !== 'boolean') throw new Error('The proposed variables contain an invalid definition.');
    const variable: TemplateVariable = { name: item.name, required: item.required };
    if ('description' in item) { if (typeof item.description !== 'string') throw new Error('A proposed variable description is invalid.'); variable.description = item.description; }
    if ('defaultValue' in item) { if (typeof item.defaultValue !== 'string') throw new Error('A proposed variable default is invalid.'); variable.defaultValue = item.defaultValue; }
    return variable;
  });
}

export function StarterProposalReview({ templateId, onAdopt }: { templateId: string; onAdopt: (patch: StarterDraftPatch) => void }): ReactElement {
  const { ask, dialog } = useConfirmation();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const request = useRef(0);
  useEffect(() => {
    request.current += 1; setProposal(null); setLoaded(false); setSelected([]); setError(null); setLoading(false);
    return () => { request.current += 1; };
  }, [templateId]);
  const load = async (): Promise<void> => {
    const current = ++request.current;
    setLoading(true); setError(null); setSelected([]); setProposal(null); setLoaded(false);
    try {
      const result = await apiFetch<Proposal | null>(`/api/v1/document-templates/${encodeURIComponent(templateId)}/starter-proposal`);
      if (current === request.current) { setProposal(result); setLoaded(true); }
    } catch (reason) { if (current === request.current) setError(formatApiError(reason, 'Could not check starter updates.')); }
    finally { if (current === request.current) setLoading(false); }
  };
  const adopt = async (): Promise<void> => {
    if (!proposal || selected.length === 0) return;
    const current = request.current;
    setError(null);
    try {
      const patch: StarterDraftPatch = {};
      for (const diff of proposal.differences.filter(d => selected.includes(d.field) && allowed.has(d.field))) {
        if (diff.field === 'Variables') patch.Variables = variablesFrom(diff.proposed);
        else {
          if (typeof diff.proposed !== 'string' && diff.proposed !== null) throw new Error(`The proposed ${diff.field} value is invalid.`);
          const value = diff.proposed ?? '';
          switch (diff.field) {
            case 'Name': patch.Name = value; break;
            case 'Description': patch.Description = value; break;
            case 'Region': patch.Region = value; break;
            case 'TargetModule': patch.TargetModule = value; break;
            case 'Body': patch.Body = value; break;
          }
        }
      }
      if (await ask({ title: 'Use starter fields in this draft?', message: `This replaces your unsaved ${selected.join(', ')} fields with the proposed starter values. Other fields stay as they are. Review the result, add a change note, and save to create a new library version.`, confirmLabel: 'Use selected fields', variant: 'destructive' }) && current === request.current) onAdopt(patch);
    } catch (reason) { setError(formatApiError(reason, 'Could not use the proposed fields.')); }
  };
  return <section aria-label="Review starter updates" style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
    {dialog}
    <button type="button" style={buttonStyle} onClick={() => void load()} disabled={loading}>{loading ? 'Checking starter…' : 'Review starter updates'}</button>
    {error && <p role="alert">{error} You can retry the check.</p>}
    {loaded && !proposal && <p>This template has no verified starter lineage. It will not be changed.</p>}
    {proposal && !proposal.requiresReview && <p>This template matches its current starter definition.</p>}
    {proposal && proposal.requiresReview && <>
      <p style={{ margin: '10px 0' }}>Compare the saved library version with the starter proposal. Select only the fields to use in your draft. Nothing is saved automatically.</p>
      {proposal.differences.map((difference, index) => <details key={`${difference.field}-${index}`} style={{ marginBottom: 8 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{difference.field}{allowed.has(difference.field) ? '' : ' — information only'}</summary>
        {allowed.has(difference.field) && <label style={{ display: 'block', margin: '8px 0' }}><input type="checkbox" checked={selected.includes(difference.field)} onChange={e => setSelected(fields => e.target.checked ? [...fields, difference.field] : fields.filter(field => field !== difference.field))} />Use updated {difference.field}</label>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}><strong>Saved library value</strong><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '8px 0', padding: 8, background: 'var(--surface2)', borderRadius: 4 }}>{show(difference.current)}</pre></div>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}><strong>Proposed starter value</strong><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '8px 0', padding: 8, background: 'var(--surface2)', borderRadius: 4 }}>{show(difference.proposed)}</pre></div>
        </div>
      </details>)}
      <button type="button" style={buttonStyle} disabled={selected.length === 0} onClick={() => void adopt()}>Use proposed fields in this draft</button>
    </>}
  </section>;
}
function show(value: unknown): string { return value == null ? '(empty)' : typeof value === 'string' ? value : JSON.stringify(value, null, 2); }
